/**
 * @fileoverview The add sheet's For picker (D50; Fable pass 2, 02d): what a
 * to-do started on Today is about, so its tick can be written in a plant's or
 * a bed's journal. First "Your garden" ("Not about one bed or plant"), then
 * one row per bed, "Bed A" over "The whole bed · 3 plants", with its plants
 * indented under it. The row picked has a check at its right. A garden with
 * more plants than OWN_TODO_SEARCH_OVER gets a search box at the top, which
 * narrows the plants (and the beds) to the words typed.
 *
 * The beds and their plants are the garden's live beds as the Beds page reads
 * them (useBeds), in the server's order; each plant's name is the server's one
 * name (lib/plantName).
 *
 * Where: OwnTodoSheet (the For row, Today only).
 * API: GET /locations?status=1 (useBeds, only while the picker is open).
 * Settings: OWN_TODO_SEARCH_OVER (here, the app's own layout value).
 * Words (en/es): todo.own.for, forGarden, forGardenSub, wholeBed, wholeBedOne,
 * wholeBedEmpty, search.
 *
 * @module own-todos/ForPicker
 */

import React from 'react';
import { Check, Search, Sprout } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useBeds } from '@/shared/hooks/useBeds';
import { BottomPanel } from '@/shared/components/ui/bottom-panel';
import { Input } from '@/shared/components/ui/input';
import { plantShown } from '@/shared/lib/plantName';
import type { OwnAbout } from '@/shared/api/ownTodo';

/** What a to-do is for, as picked: the garden, a bed or a plant, with the words the For row shows. */
export interface ForChoice {
  about: OwnAbout;
  /** The bed (about bed) or the plant (about plant); null for the garden. */
  id: number | null;
  /** Its name for the For row; '' for the garden (the row says "Your garden"). */
  name: string;
}

/** The picker's props. */
interface ForPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The choice now, checked. */
  value: ForChoice;
  /** Told the choice picked; the picker closes. */
  onPick: (choice: ForChoice) => void;
}

/**
 * The picker gets a search box once the garden has more plants than this (Fable pass 2, 02d). A count.
 * @kept K5
 */
const OWN_TODO_SEARCH_OVER = 10;

/** A row of the picker: thumb-tall, its words at the left and the check at its right. */
const ROW = 'flex min-h-12 w-full items-center gap-3 py-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/**
 * The For picker.
 *
 * @param props - ForPickerProps.
 */
export const ForPicker: React.FC<ForPickerProps> = ({ open, onOpenChange, value, onPick }) => {
  const { reactTranslate: t } = useTranslation();
  const { data: beds } = useBeds(open);
  const [search, setSearch] = React.useState('');
  /** Every bed with its plants growing now. */
  const list = React.useMemo(() => (beds ?? []).map((bed) => ({
    bed,
    plants: bed.plants.filter((plant) => plant.status === 1),
  })), [beds]);
  const plantCount = list.reduce((sum, row) => sum + row.plants.length, 0);
  const searching = plantCount > OWN_TODO_SEARCH_OVER;
  const words = searching ? search.trim().toLocaleLowerCase() : '';
  /** The beds and plants the search leaves: a bed whose name matches keeps all its plants. */
  const shown = words
    ? list.map(({ bed, plants }) => (bed.name.toLocaleLowerCase().includes(words)
      ? { bed, plants }
      : { bed, plants: plants.filter((plant) => plantShown(plant).toLocaleLowerCase().includes(words)) }))
      .filter((row) => row.plants.length > 0 || row.bed.name.toLocaleLowerCase().includes(words))
    : list;

  /**
   * Whether a row is the choice now.
   *
   * @param about - The row's kind.
   * @param id - Its bed or plant; null for the garden.
   */
  const picked = (about: OwnAbout, id: number | null): boolean => value.about === about && value.id === id;
  /** The check at a picked row's right. */
  const check = <Check className="ml-auto h-5 w-5 shrink-0 text-primary" aria-hidden="true" />;

  return (
    <BottomPanel open={open} onOpenChange={onOpenChange} title={t('todo.own.for')}>
      {searching && (
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('todo.own.search')}
            aria-label={t('todo.own.search')}
            className="h-12 pl-9 text-base"
          />
        </div>
      )}
      <div role="listbox" aria-label={t('todo.own.for')} className="divide-y">
        <button type="button" role="option" aria-selected={picked('garden', null)} onClick={() => onPick({ about: 'garden', id: null, name: '' })} className={cn(ROW, picked('garden', null) && 'bg-primary/10')}>
          <span className="min-w-0">
            <span className="block font-semibold">{t('todo.own.forGarden')}</span>
            <span className="block text-sm text-muted-foreground">{t('todo.own.forGardenSub')}</span>
          </span>
          {picked('garden', null) && check}
        </button>
        {shown.map(({ bed, plants }) => {
          const count = bed.plants.filter((plant) => plant.status === 1).length;
          return (
            <div key={bed.id} className="py-1">
              <button type="button" role="option" aria-selected={picked('bed', bed.id)} onClick={() => onPick({ about: 'bed', id: bed.id, name: bed.name })} className={cn(ROW, picked('bed', bed.id) && 'bg-primary/10')}>
                <span className="min-w-0">
                  <span className="block font-semibold">{bed.name}</span>
                  <span className="block text-sm text-muted-foreground">
                    {count === 0 ? t('todo.own.wholeBedEmpty') : count === 1 ? t('todo.own.wholeBedOne') : t('todo.own.wholeBed', { count })}
                  </span>
                </span>
                {picked('bed', bed.id) && check}
              </button>
              {plants.map((plant) => (
                <button
                  key={plant.plant_obj_id}
                  type="button"
                  role="option"
                  aria-selected={picked('plant', plant.plant_obj_id)}
                  onClick={() => onPick({ about: 'plant', id: plant.plant_obj_id, name: plantShown(plant) })}
                  className={cn(ROW, 'pl-4', picked('plant', plant.plant_obj_id) && 'bg-primary/10')}
                >
                  <Sprout className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  <span className="min-w-0">{plantShown(plant)}</span>
                  {picked('plant', plant.plant_obj_id) && check}
                </button>
              ))}
            </div>
          );
        })}
      </div>
    </BottomPanel>
  );
};
